import type { LabResultRecord } from '../../types/labs';
import { resolveBiomarker } from '../../data/biomarkerCatalogue';
import { computeBiomarkerStatus } from '../biomarkerStatus';
import { coerceDate } from '../dateLabels';

/**
 * "Your results are back" — the newest batch of lab results, and whether the
 * member has seen it yet.
 *
 * This exists because the single most important event in the product had no
 * representation anywhere on Home. Results landed from Inuvi, clinicians were
 * alerted, the member was told nothing, and "What's changed" — which is
 * delta-based and needs two readings of a marker to say anything — renders
 * empty for exactly the first panel. A member's first blood work arrived to
 * silence.
 *
 * A "batch" is a draw, identified by the day the results were observed. Real
 * panels come back with a spread of observation timestamps across a few hours
 * of analyser time, so grouping on the day is what reconstructs the event the
 * member actually experienced: one blood draw, one set of numbers.
 */

/** How recent a batch has to be before Home will lead with it. */
export const NEW_RESULTS_HORIZON_DAYS = 30;

export interface ResultDraw {
  /** Stable identifier for the draw — the day it was taken. */
  key: string;
  /** Markers in this draw. */
  count: number;
  /** How many of them fall outside the reference range applied to them. */
  outOfRangeCount: number;
  /** Markers we could not place on a range — never folded into either count. */
  notComparedCount: number;
  /** Latest observation timestamp in the draw. */
  observedAt: Date;
  /**
   * When the blood was actually taken, where the lab reported it (HL7 OBR-7).
   * This is the date the member remembers — they know when they were at the
   * clinic, not when an analyser got round to their sample — so it is what the
   * page shows when it is available.
   */
  collectedAt: Date | null;
  /** What the draw was requisitioned as, e.g. "Baseline (men)". */
  panelLabel: string | null;
  /** Who performed it, where we know. */
  labLabel: string | null;
  /** Result document ids in this draw, so a caller can mark them. */
  recordIds: string[];
}

export interface LatestResultsBatch {
  /** Markers in the newest batch. */
  count: number;
  /** How many of them fall outside the reference range applied to them. */
  outOfRangeCount: number;
  /** Markers we could not place on a range — never folded into either count. */
  notComparedCount: number;
  /** When the batch was observed. */
  observedAt: Date;
  /** True when this batch is the member's only one — no prior panel exists. */
  isFirstPanel: boolean;
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Human label for the performing lab. Only names labs we actually integrate. */
function labLabelFor(record: LabResultRecord): string | null {
  const vendor = (record.sourceVendor || '').toLowerCase();
  if (vendor === 'inuvi') return 'Inuvi';
  if (record.sourceSystem === 'inuvi_hl7') return 'Inuvi';
  // A member-entered value is not a lab result and must not be dressed as one.
  if (record.sourceType === 'manual_lab_entry') return 'Added by you';
  return null;
}

/**
 * Every draw on file, newest first.
 *
 * A draw is keyed on the day the blood was TAKEN where the lab told us, and on
 * the observation day otherwise. Those differ in practice: a Friday afternoon
 * draw can be analysed on Saturday, and grouping on the analyser's clock would
 * split one visit to the clinic into two events the member never experienced.
 */
export function listResultDraws(labs: LabResultRecord[], sex: string | null): ResultDraw[] {
  interface Bucket {
    observedAt: Date;
    collectedAt: Date | null;
    records: LabResultRecord[];
  }

  const buckets = new Map<string, Bucket>();
  for (const record of labs) {
    const observedAt = coerceDate(record.observedAt);
    if (!observedAt) continue;
    const collectedAt = coerceDate(record.collectedAt);
    const key = dayKey(collectedAt || observedAt);

    const bucket = buckets.get(key) || { observedAt, collectedAt, records: [] };
    if (observedAt > bucket.observedAt) bucket.observedAt = observedAt;
    if (!bucket.collectedAt && collectedAt) bucket.collectedAt = collectedAt;
    bucket.records.push(record);
    buckets.set(key, bucket);
  }

  return [...buckets.entries()]
    .map(([key, bucket]) => {
      let outOfRangeCount = 0;
      let notComparedCount = 0;
      for (const record of bucket.records) {
        const meta = resolveBiomarker({ biomarkerKey: record.biomarkerKey, biomarker: record.biomarker });
        if (!meta) {
          // An unrecognised marker is uncounted, not in-range. Same rule the
          // snapshot chip follows: never imply a verdict we did not reach.
          notComparedCount += 1;
          continue;
        }
        const status = computeBiomarkerStatus(record, meta, sex);
        if (status.status === 'unknown') notComparedCount += 1;
        else if (status.status !== 'in_range') outOfRangeCount += 1;
      }

      // `panel` is a property of the REQUISITION, so within one draw every row
      // that carries it should agree. Where they disagree the field is not a
      // requisition label at all — some sources write a per-marker domain into
      // it, which is how a real staging record produced a whole 59-marker panel
      // headed "Liver". Name the draw only when the record actually agrees on
      // its name; a bare date is honest, an arbitrary pick is not.
      const panels = new Set(
        bucket.records.map((record) => record.panel).filter((value): value is string => Boolean(value)),
      );
      const panelLabel = panels.size === 1 ? [...panels][0] : null;

      const labs_ = new Set(
        bucket.records.map(labLabelFor).filter((value): value is string => Boolean(value)),
      );
      const labLabel = labs_.size === 1 ? [...labs_][0] : null;

      return {
        key,
        count: bucket.records.length,
        outOfRangeCount,
        notComparedCount,
        observedAt: bucket.observedAt,
        collectedAt: bucket.collectedAt,
        panelLabel,
        labLabel,
        recordIds: bucket.records.map((record) => record.id),
      };
    })
    .sort((left, right) => right.observedAt.getTime() - left.observedAt.getTime());
}

/**
 * Summarises the most recent batch of results, or null when there is nothing to
 * summarise. Pure — the caller supplies `now` and the member's sex, so this can
 * be tested as a truth table.
 */
export function summariseLatestResultsBatch(
  labs: LabResultRecord[],
  sex: string | null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  now: Date = new Date(),
): LatestResultsBatch | null {
  const draws = listResultDraws(labs, sex);
  if (draws.length === 0) return null;
  const newest = draws[0];
  return {
    count: newest.count,
    outOfRangeCount: newest.outOfRangeCount,
    notComparedCount: newest.notComparedCount,
    observedAt: newest.observedAt,
    isFirstPanel: draws.length === 1,
  };
}

/**
 * Whether Home should lead with this batch: the member has not seen it, and it
 * is recent enough that "your results are back" is still true rather than
 * merely unacknowledged.
 *
 * The horizon matters because `seenAt` lives in localStorage, the same place
 * the opened-reports receipt lives. A member signing in on a new device has no
 * watermark at all, and without a horizon they would be greeted by a
 * two-year-old panel announcing itself as news.
 */
export function isBatchUnseen(
  batch: LatestResultsBatch,
  seenAt: Date | null,
  now: Date = new Date(),
): boolean {
  const ageDays = (now.getTime() - batch.observedAt.getTime()) / (24 * 60 * 60 * 1000);
  if (ageDays > NEW_RESULTS_HORIZON_DAYS) return false;
  if (!seenAt) return true;
  return batch.observedAt.getTime() > seenAt.getTime();
}
